import { useState } from 'react';
import type { MeetingDetail, SourceReference, VisualizationKind } from '@shared/contract';
import { api } from '../lib/api';
import { ImageKey } from './ImageKey';
import { Receipt } from './Receipt';

const KINDS: { kind: VisualizationKind; label: string; about: string; note: string }[] = [
  {
    kind: 'receipt',
    label: 'Kvitto',
    about: 'Mötet som ett kvitto: längd, beslut, att göra och det som blev kvar. Bra som avslutning i rummet.',
    note: 'Raderna med tid leder till rätt ställe i transkriptet.',
  },
  {
    kind: 'comic',
    label: 'Serie',
    about: 'Mötet som en serie i fyra eller sex rutor: hur det började, vändpunkterna och hur det kändes.',
    note: 'Fritt efter mötet. Figurerna föreställer inte deltagarna och replikerna är inga citat. Rutorna läses från vänster till höger.',
  },
  {
    kind: 'sketchnote',
    label: 'Grafisk dokumentation',
    about: 'En tecknad affisch med mötets ämnen längs en stig, besluten, det olösta och vem som gör vad.',
    note: 'Bilden är AI:ns tolkning av mötet.',
  },
  {
    kind: 'city',
    label: 'Staden',
    about: 'Varje ämne är en byggnad. Höjden visar tiden, monument är beslut, byggarbetsplatser är olöst och en tom tomt är något ingen tog ansvar för.',
    note: 'Bilden är AI:ns tolkning av mötet.',
  },
  {
    kind: 'rocket',
    label: 'Raketen',
    about: 'Mötet som en raket som byggs nerifrån och upp i den ordning ämnena kom. Ett hål betyder att ingen tog ansvar, och då lyfter den inte.',
    note: 'Bilden är AI:ns tolkning av mötet.',
  },
];

const TAB_KEY = 'mr.visualization';

function readTab(): VisualizationKind {
  try {
    const v = localStorage.getItem(TAB_KEY);
    return KINDS.some((k) => k.kind === v) ? (v as VisualizationKind) : 'receipt';
  } catch {
    return 'receipt';
  }
}

interface Props {
  detail: MeetingDetail;
  onJump: (ref: SourceReference) => void;
}

export function Visualizations({ detail, onJump }: Props) {
  const [tab, setTab] = useState<VisualizationKind>(readTab);
  const [requesting, setRequesting] = useState<VisualizationKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const current = KINDS.find((k) => k.kind === tab)!;
  const viz = detail.visualizations.find((v) => v.kind === tab);
  const working = requesting === tab || viz?.status === 'pending' || viz?.status === 'running';
  const date = new Date(detail.started_at ?? detail.created_at).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });

  function select(kind: VisualizationKind) {
    setTab(kind);
    setError(null);
    try {
      localStorage.setItem(TAB_KEY, kind);
    } catch {
      /* ignoreras */
    }
  }

  async function create() {
    setError(null);
    setRequesting(tab);
    try {
      await api.visualize(detail.id, tab);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRequesting(null);
    }
  }

  return (
    <section className="card visualizations">
      <div className="card-head">
        <h3>Visualisera mötet</h3>
        {viz?.status === 'done' && (
          <div className="row">
            {viz.image_url && (
              <a className="btn small" href={viz.image_url} download={`${detail.title} ${current.label}.png`}>
                Ladda ner
              </a>
            )}
            <button className="btn small" onClick={create} disabled={working}>
              Gör om
            </button>
          </div>
        )}
      </div>
      <div className="viz-tabs" role="tablist">
        {KINDS.map((k) => (
          <button
            key={k.kind}
            role="tab"
            aria-selected={k.kind === tab}
            className={`chip${k.kind === tab ? ' active' : ''}`}
            onClick={() => select(k.kind)}
          >
            {k.label}
          </button>
        ))}
      </div>
      <p className="muted small">{current.about}</p>

      {error && <div className="alert error small">{error}</div>}
      {viz?.status === 'failed' && !working && (
        <div className="alert error small">{viz.error ?? 'Visualiseringen kunde inte skapas.'}</div>
      )}

      {working ? (
        <div className="viz-working">
          <span className="spinner" aria-hidden />
          <span className="muted">
            {tab === 'receipt' ? 'Läser igenom mötet…' : 'Läser igenom mötet och ritar bilden. Det tar oftast under en minut.'}
          </span>
        </div>
      ) : viz?.status === 'done' && viz.analysis ? (
        <div className="viz-result">
          {tab === 'receipt' ? (
            <Receipt analysis={viz.analysis} title={detail.title} date={date} onJump={onJump} />
          ) : (
            viz.image_url && (
              <a href={viz.image_url} target="_blank" rel="noreferrer" title="Öppna i full storlek">
                <img className="viz-image" src={viz.image_url} alt={`${current.label} av mötet ${detail.title}`} />
              </a>
            )
          )}
          <p className="muted small">{current.note}</p>
          {tab !== 'receipt' && <ImageKey kind={tab} analysis={viz.analysis} onJump={onJump} />}
        </div>
      ) : (
        <button className="btn primary" onClick={create}>
          {viz?.status === 'failed' ? 'Försök igen' : `Skapa ${current.label.toLocaleLowerCase('sv-SE')}`}
        </button>
      )}
    </section>
  );
}
